// Pure helpers behind KnowledgeCheckManager, kept out of the component so
// the write payload can be unit-tested. Firestore rejects `undefined`
// anywhere in a document, so optional fields are left out, never set to
// undefined.

import type { MultipleChoiceQuestion } from "../../types/course";

export interface QuestionDraft {
  question: string;
  options: string[];
  correctAnswerIndex: number;
  explanation: string;
}

export const OPTION_LETTERS = ["A", "B", "C", "D"];

export function emptyQuestionDraft(): QuestionDraft {
  return { question: "", options: ["", "", "", ""], correctAnswerIndex: 0, explanation: "" };
}

export function questionToDraft(q: MultipleChoiceQuestion): QuestionDraft {
  return {
    question: q.question,
    options: [...q.options, "", "", "", ""].slice(0, 4),
    correctAnswerIndex: q.correctAnswerIndex,
    explanation: q.explanation ?? "",
  };
}

// Validates the draft and returns the question to store, or the message to
// show. `id` is the existing question's id when editing.
export function buildQuestion(
  draft: QuestionDraft,
  id: string = `q_${Date.now()}`,
): { question: MultipleChoiceQuestion } | { error: string } {
  const text = draft.question.trim();
  const options = draft.options.map((o) => o.trim());
  if (!text) return { error: "Please enter the question." };
  if (options.length !== 4 || options.some((o) => !o)) return { error: "Please fill in all four options." };
  if (draft.correctAnswerIndex < 0 || draft.correctAnswerIndex > 3) return { error: "Pick the correct answer." };
  const explanation = draft.explanation.trim();
  return {
    question: {
      id,
      question: text,
      options,
      correctAnswerIndex: draft.correctAnswerIndex,
      ...(explanation ? { explanation } : {}),
    },
  };
}
